// Groups (circles + networks) mutations. Each hook calls the API directly via
// `getApiClient()` and patches the QueryClient cache. Server actions are not
// involved — pages prefetch via the existing `lib/views/groups.ts` fetchers and
// client interactions go through these hooks.
//
// Invalidations are scoped to the groups slice. Cross-feature effects (e.g.
// joining a circle ought to bump feed scope counts) are not handled here —
// keep the cache patches local; feed code can refresh itself on focus.

import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import {
  groupKindToType,
  trackBroadcastCreated,
  trackGroupCreated,
  trackGroupJoined,
  trackGroupLeft,
  trackMemberInvited,
  trackMemberRoleChanged,
  trackNetworkLeaderApplicationSubmitted,
  type JoinMethod,
} from '../analytics/events';
import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import {
  invalidateAndRefetchAll,
  patchAllInfiniteLists,
  patchAllLists,
  removeItemFromLists,
  restoreSnapshots,
  type SnapshotSet,
} from '../query-cache-utils';
import {
  invalidateAfterCircleNetworkLink,
  invalidateGroupAfterMutation,
} from '../cache/group-invalidation';
import {
  broadcastKeys,
  groupBroadcastScope,
  type AdminBroadcastDetail,
  type BroadcastDoc,
  type BroadcastGroupKind,
  type BroadcastPreview,
} from '../queries/broadcasts';
import {
  groupKeys,
  type CircleDetailResponse,
  type CirclePreview,
  type GroupDetail,
  type GroupJoinLink,
  type GroupImageContentType as GroupImageContentTypeFromQueries,
  type GroupImageField as GroupImageFieldFromQueries,
  type GroupImageKind,
  type GroupSummary,
  type MembershipRequestView,
  type ViewerGroupRole,
} from '../queries/groups';
import { spacesKeys } from '../queries/spaces';
import { applySpaceJoinPatch } from './spaces';
import { completeOnboardingActionIfPending, onboardingKeys } from '../../components/onboarding/progress';

// Server-emitted error codes the unified members mutations surface so call
// sites can show targeted messages. The server returns HTTP 409 with one of
// these codes when the last leader would be left orphaned; everything else
// is rendered via the message from the wire envelope.
export const GROUP_MEMBER_ERROR_CODES = {
  cannotDemoteLastLeader: 'groups/cannot_demote_last_leader',
  cannotRemoveLastLeader: 'groups/cannot_remove_last_leader',
} as const;

export type JoinGroupResponse = components['schemas']['JoinGroupResponse'];

// ---------- Membership optimistic-cache helpers ----------
//
// Join / leave / withdraw / approve / decline mutations all flip the same
// derived state on a `GroupSummary`-shaped row: `viewerMembership` (member
// became/left) or `viewerJoinRequestStatus` (request opened/withdrawn).
// Both the all-circles / all-networks lists (infinite + flat shapes) and
// the per-group detail caches need to track the flip so consumer buttons
// re-derive their label from cache instantly. Helpers below own the patch
// + snapshot/rollback contract so each mutation hook stays small.

type MembershipPatch = (summary: GroupSummary) => GroupSummary;

function patchAllGroupListsInCache(
  qc: QueryClient,
  groupId: string,
  patch: MembershipPatch,
): SnapshotSet {
  const apply = (items: readonly GroupSummary[]): readonly GroupSummary[] =>
    items.map((g) => (g.id === groupId ? patch(g) : g));
  // Circles + networks share the `GroupSummary` row shape, and both register
  // under `groupKeys.<kind>.all` (covers `.lists()` infinite + `.mine()` flat
  // + locations + etc.). Pass through the `all` prefix so every cached list
  // gets patched — the predicate of `items?.[0]?.id === groupId` doesn't
  // hold because items is an unrelated shape on non-list keys; the helpers
  // filter to list-shaped values internally.
  const inf = patchAllInfiniteLists<{ items: readonly GroupSummary[] }, GroupSummary>(
    qc,
    groupKeys.all,
    apply,
  );
  const flat = patchAllLists<{ items: readonly GroupSummary[] }, GroupSummary>(
    qc,
    groupKeys.all,
    apply,
  );
  return { previousLists: [...inf.previousLists, ...flat.previousLists] };
}

// Patch the circle/network detail caches for a given id. Circles use a
// discriminated `GetCircleResponse` (visitor → `CirclePreview`, member /
// leader → `GroupDetail`); networks use a plain `GroupDetail`. Both inner
// shapes carry the same membership / join-request fields, so callers pass
// one patch function that operates on the relevant subset.
type DetailPatch = {
  membership?: 'member' | 'leader' | null;
  joinRequestStatus?: 'pending' | 'approved' | 'declined' | null;
};

function patchCircleAndNetworkDetail(
  qc: QueryClient,
  groupId: string,
  patch: DetailPatch,
): SnapshotSet {
  const previousLists: Array<readonly [readonly unknown[], unknown]> = [];

  const circleKey = groupKeys.circle.detail(groupId).queryKey;
  const circle = qc.getQueryData<CircleDetailResponse>(circleKey);
  if (circle !== undefined) {
    previousLists.push([circleKey, circle]);
    if (circle.access === 'visitor') {
      const nextInner: CirclePreview = {
        ...circle.circle,
        ...(patch.joinRequestStatus !== undefined
          ? { viewerJoinRequestStatus: patch.joinRequestStatus }
          : {}),
      };
      qc.setQueryData<CircleDetailResponse>(circleKey, {
        ...circle,
        circle: nextInner,
      });
    } else {
      const nextInner: GroupDetail = {
        ...circle.circle,
        ...(patch.membership !== undefined
          ? { viewerMembership: patch.membership }
          : {}),
        ...(patch.joinRequestStatus !== undefined
          ? { viewerJoinRequestStatus: patch.joinRequestStatus }
          : {}),
      };
      qc.setQueryData<CircleDetailResponse>(circleKey, {
        ...circle,
        circle: nextInner,
      });
    }
  }

  const networkKey = groupKeys.network.detail(groupId).queryKey;
  const network = qc.getQueryData<GroupDetail>(networkKey);
  if (network !== undefined) {
    previousLists.push([networkKey, network]);
    qc.setQueryData<GroupDetail>(networkKey, {
      ...network,
      ...(patch.membership !== undefined
        ? { viewerMembership: patch.membership }
        : {}),
      ...(patch.joinRequestStatus !== undefined
        ? { viewerJoinRequestStatus: patch.joinRequestStatus }
        : {}),
    });
  }

  return { previousLists };
}

// Combined: patch every list-shape + detail-shape cache for one group id.
// Returns a single rollback bag that `restoreSnapshots` reverts on error.
function applyJoinPatch(
  qc: QueryClient,
  groupId: string,
  patch: DetailPatch,
): SnapshotSet {
  const lists = patchAllGroupListsInCache(qc, groupId, (g) => ({
    ...g,
    ...(patch.membership !== undefined
      ? { viewerMembership: patch.membership }
      : {}),
    ...(patch.joinRequestStatus !== undefined
      ? { viewerJoinRequestStatus: patch.joinRequestStatus }
      : {}),
  }));
  const detail = patchCircleAndNetworkDetail(qc, groupId, patch);
  return {
    previousLists: [...lists.previousLists, ...detail.previousLists],
  };
}

// "Optimistically open a join" — at click time we don't know whether the
// server will return `joined` (open policy) or `requested` (gated). The
// summary's `joinPolicy` field tells us which to predict so the CTA flips
// to the right destination state immediately. `onSettled` invalidate +
// refetch is the final authority if the prediction was wrong.
function predictJoinPatch(
  qc: QueryClient,
  groupId: string,
): DetailPatch {
  // Find any cached summary for this group to read its joinPolicy. We
  // check lists (most common) before details because lists hold rows for
  // every group the viewer has browsed; details are 0-or-1 per id.
  type MaybeListShape = {
    readonly items?: readonly GroupSummary[];
    readonly pages?: ReadonlyArray<{ readonly items: readonly GroupSummary[] }>;
  };
  const probe = qc.getQueriesData<unknown>({ queryKey: groupKeys.all });
  for (const [, value] of probe) {
    if (value === null || typeof value !== 'object') continue;
    const v = value as MaybeListShape;
    if (v.pages !== undefined && Array.isArray(v.pages)) {
      for (const page of v.pages) {
        const hit = page.items.find((g: GroupSummary) => g.id === groupId);
        if (hit !== undefined) {
          return hit.joinPolicy === 'open'
            ? { membership: 'member' }
            : { joinRequestStatus: 'pending' };
        }
      }
    } else if (v.items !== undefined && Array.isArray(v.items)) {
      const hit = v.items.find((g: GroupSummary) => g.id === groupId);
      if (hit !== undefined) {
        return hit.joinPolicy === 'open'
          ? { membership: 'member' }
          : { joinRequestStatus: 'pending' };
      }
    }
  }
  // Fall back to detail caches.
  const network = qc.getQueryData<GroupDetail>(
    groupKeys.network.detail(groupId).queryKey,
  );
  if (network !== undefined) {
    return network.joinPolicy === 'open'
      ? { membership: 'member' }
      : { joinRequestStatus: 'pending' };
  }
  const circle = qc.getQueryData<CircleDetailResponse>(
    groupKeys.circle.detail(groupId).queryKey,
  );
  if (circle !== undefined) {
    return circle.circle.joinPolicy === 'open'
      ? { membership: 'member' }
      : { joinRequestStatus: 'pending' };
  }
  // Unknown policy — assume request (gated) so a wrong guess shows a pending
  // pill that the server-truth flip corrects, rather than appearing to join
  // a gated group.
  return { joinRequestStatus: 'pending' };
}

// Remove a join request row from every cached requests list for a group. The
// leader cards back onto `{ items: MembershipRequestView[] }` (the unified
// `direction='request'` slice), so the row id is `MembershipRequestView.id`.
function removeJoinRequestFromCache(
  qc: QueryClient,
  groupId: string,
  kind: 'circle' | 'network',
  requestId: string,
): SnapshotSet {
  const key =
    kind === 'circle'
      ? groupKeys.circle.joinRequests(groupId).queryKey
      : groupKeys.network.joinRequests(groupId).queryKey;
  return removeItemFromLists<MembershipRequestView>(qc, key, requestId);
}

// ---------- Circles: create / update / lifecycle ----------

export type CreateCircleInput = {
  name: string;
  description?: string;
  topicIds: readonly string[];
  format?: 'in_person' | 'virtual' | 'hybrid';
  access: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  joinPolicy?: 'open' | 'request' | 'invite_only';
  maxMembers?: number;
  language?: string;
  imageUrl?: string | null;
  /** Structured place payload from `PlacePicker`. `null` clears any prior
   *  selection. */
  place?: {
    placeId: string;
    placeText: string;
    placeLat: number;
    placeLng: number;
    placeCountryCode: string | null;
  } | null;
  affiliatedWithCompany?: boolean | null;
  affiliatedCompany?: string | null;
  // When set, the new circle is created already linked to this network.
  // The server enforces caller-is-leader of the network and that the
  // target is actually a network. Used by the network admin "Start a
  // Circle" CTA so we don't have to chain create → link from the UI.
  parentNetworkId?: string;
};

export function useCreateCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCircleInput) => {
      const client = getApiClient();
      const data = unwrap(await client.POST('/api/v1/circles', {
        body: {
          name: input.name,
          description: input.description,
          topicIds: [...input.topicIds],
          format: input.format,
          access: input.access,
          minReadRole: input.minReadRole ?? 'member',
          joinPolicy: input.joinPolicy,
          maxMembers: input.maxMembers,
          language: input.language,
          imageUrl: input.imageUrl ?? undefined,
          place: input.place ?? undefined,
          affiliatedWithCompany: input.affiliatedWithCompany ?? undefined,
          affiliatedCompany: input.affiliatedCompany ?? undefined,
          parentNetworkId: input.parentNetworkId,
        },
      }));
      return data.data;
    },
    onSuccess: (_data, input) => {
      trackGroupCreated({
        group_type: 'circle',
        access: input.access,
        ...(input.joinPolicy !== undefined
          ? { join_policy: input.joinPolicy }
          : {}),
      });
      qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
      // "My circles" filter view is usually inactive when a join/leave fires
      // (user is browsing all-circles). Default invalidate marks it stale but
      // doesn't refetch — the next tab switch would flash stale rows.
      // `circle.mine` is now a leaf keyed by { includeArchived, pageSize }, so
      // refetch every variant via the opts-less prefix.
      invalidateAndRefetchAll(qc, {
        queryKey: [...groupKeys.circle.all, 'mine'],
      });
      // The onboarding progress response includes `leaderCircleId` — creating a
      // circle makes the creator a leader, so the cached progress is now stale.
      // The completion screen reads `leaderCircleId` to deep-link the checklist
      // CTAs; without this invalidation it sees null and falls back to /circles.
      qc.invalidateQueries({ queryKey: onboardingKeys.progress() });
      // If the circle was created already linked to a network, refresh the
      // network's circles list so the admin surface shows the new row.
      // Prefix-match invalidates both the active and includeArchived
      // variants the key builder emits.
      if (input.parentNetworkId !== undefined) {
        qc.invalidateQueries({
          queryKey: [
            ...groupKeys.network.all,
            'circles',
            input.parentNetworkId,
          ] as const,
        });
      }
    },
  });
}

export type UpdateCircleInput = {
  circleId: string;
  name?: string;
  description?: string | null;
  format?: 'in_person' | 'virtual' | 'hybrid';
  access?: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  joinPolicy?: 'open' | 'request' | 'invite_only';
  maxMembers?: number | null;
  language?: string | null;
  /** Structured place payload from `PlacePicker`. `null` clears the column. */
  place?: {
    placeId: string;
    placeText: string;
    placeLat: number;
    placeLng: number;
    placeCountryCode: string | null;
  } | null;
  imageUrl?: string | null;
  logoUrl?: string | null;
  topicIds?: readonly string[];
  tags?: readonly string[];
  affiliatedWithCompany?: boolean | null;
  affiliatedCompany?: string | null;
  expectedVersion?: number;
};

export function useUpdateCircle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCircleInput) => {
      const client = getApiClient();
      return unwrap(await client.PATCH('/api/v1/circles/{id}', {
        params: { path: { id: input.circleId } },
        body: {
          name: input.name,
          description: input.description,
          format: input.format,
          access: input.access,
          minReadRole: input.minReadRole ?? 'member',
          joinPolicy: input.joinPolicy,
          maxMembers: input.maxMembers,
          language: input.language,
          place: input.place,
          imageUrl: input.imageUrl,
          logoUrl: input.logoUrl,
          topicIds:
            input.topicIds !== undefined ? [...input.topicIds] : undefined,
          tags: input.tags !== undefined ? [...input.tags] : undefined,
          affiliatedWithCompany: input.affiliatedWithCompany,
          affiliatedCompany: input.affiliatedCompany,
          expectedVersion: input.expectedVersion,
        },
      }));
    },
    onSuccess: (_data, vars) => {
      invalidateGroupAfterMutation(qc, 'circle', vars.circleId, 'update');
    },
  });
}

// Start the circle's group chat on demand. Idempotent server-side, so a
// double-click or a click on a circle that already has a chat just resolves
// to the existing conversation id.
export function useStartCircleGroupChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (circleId: string) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/circles/{id}/conversation',
        { params: { path: { id: circleId } } },
      ));
      return data.data;
    },
    onSuccess: (_data, circleId) => {
      qc.invalidateQueries({ queryKey: groupKeys.circle.conversation(circleId).queryKey });
    },
  });
}

// Group image upload — two-step flow shared between logos and covers across
// circles, networks, and spaces. After the URL collapse both routes live
// under `/groups/{id}/{cover|logo}(/commit)?` and the server resolves the
// row's kind before delegating. The `kind` arg here is still consumed for
// query-key invalidation; the URL is the same regardless. Commit returns
// the updated `GroupDetail` so consumers can show the new image immediately.
//
// `groupImageSupports(kind, field)` in `queries/groups.ts` is the canonical
// render-time gate — don't render the "Upload logo" CTA on a kind that
// doesn't support it; the server returns 422 if a client tries anyway.

/** Re-export so consumers that already import from this module don't need
 *  to chase the now-canonical query module to type a prop. */
export type GroupKind = GroupImageKind;
export type GroupImageField = GroupImageFieldFromQueries;
export type GroupImageContentType = GroupImageContentTypeFromQueries;

type UploadGroupImageResponse = components['schemas']['UploadGroupImageResponse'];
// After URL collapse, the unified `/groups/{id}/{cover|logo}/commit` route
// returns `GroupDetail` for every kind including Spaces. The server projects
// the row through `toGroupDetail`, which carries the superset of fields any
// kind reads (Spaces consumers ignore the network-only / circle-only ones).
export type CommittedGroupImage = components['schemas']['GroupDetail'];

export function useRequestGroupImageUpload(_kind: GroupKind, field: GroupImageField) {
  return useMutation({
    mutationFn: async (input: {
      identifier: string;
      contentType: GroupImageContentType;
    }): Promise<UploadGroupImageResponse> => {
      const client = getApiClient();
      const path = { id: input.identifier };
      const body = { contentType: input.contentType };
      if (field === 'logo') {
        return unwrap(await client.POST('/api/v1/groups/{id}/logo', {
          params: { path },
          body,
        }));
      }
      return unwrap(await client.POST('/api/v1/groups/{id}/cover', {
        params: { path },
        body,
      }));
    },
  });
}

export function useCommitGroupImage(kind: GroupKind, field: GroupImageField) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      identifier: string;
      key: string;
      // Required, not optional: the server treats a commit without
      // originalKey/crop as "clear stored originals" (legacy-row shape).
      // Every client commit uploads the original alongside the render, so
      // the type forces callers to send both.
      originalKey: string;
      crop: { x: number; y: number; width: number; height: number };
    }): Promise<CommittedGroupImage> => {
      const client = getApiClient();
      const path = { id: input.identifier };
      const body = {
        key: input.key,
        originalKey: input.originalKey,
        crop: input.crop,
      };
      if (field === 'logo') {
        const data = unwrap(await client.POST(
          '/api/v1/groups/{id}/logo/commit',
          { params: { path }, body },
        ));
        return data.data;
      }
      const data = unwrap(await client.POST(
        '/api/v1/groups/{id}/cover/commit',
        { params: { path }, body },
      ));
      return data.data;
    },
    onSuccess: (_data, vars) => {
      if (kind === 'circle') {
        qc.invalidateQueries({ queryKey: groupKeys.circle.detail(vars.identifier).queryKey });
        qc.invalidateQueries({ queryKey: groupKeys.circle.lists() });
      } else if (kind === 'network') {
        qc.invalidateQueries({ queryKey: groupKeys.network.detail(vars.identifier).queryKey });
        qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
      } else {
        qc.invalidateQueries({ queryKey: spacesKeys.detail(vars.identifier).queryKey });
        qc.invalidateQueries({ queryKey: spacesKeys.lists() });
      }
    },
  });
}

// Kind-agnostic archive / restore. The same hooks drive circle, network,
// and space admin pages — the server resolves the group's kind from the id.
// Callers don't need to know what they're archiving.
export function useArchiveGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = getApiClient();
      unwrapVoid(await client.POST(
        '/api/v1/groups/{id}/archive',
        { params: { path: { id: groupId } } },
      ));
    },
    onSettled: (_data, _err, groupId) => {
      // Archive endpoints are kind-agnostic — pass 'circle' as a sentinel;
      // the helper fans across all three kinds for the 'archive' cause.
      invalidateGroupAfterMutation(qc, 'circle', groupId, 'archive');
    },
  });
}

export function useRestoreGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/restore',
        { params: { path: { id: groupId } } },
      ));
    },
    onSettled: (_data, _err, groupId) => {
      invalidateGroupAfterMutation(qc, 'circle', groupId, 'archive');
    },
  });
}

// ---------- Group membership (request / withdraw / leave / decisions) ----------
//
// Unified hooks across all three kinds (circle / network / space). The
// underlying `POST /api/v1/groups/{id}/join` endpoint is kind-agnostic —
// the service dispatches on `groups.kind` internally for any kind-specific
// side effects. The hook still takes `kind` because the cache-patching
// logic differs: circles + networks share `applyJoinPatch` (predicts the
// outcome from cached `joinPolicy`), spaces use `applySpaceJoinPatch`
// because space summaries carry `viewerRole` instead of `viewerMembership`.

export type JoinGroupInput = {
  readonly id: string;
  readonly kind: GroupKind;
  // Analytics-only — how the join was initiated (endpoint doesn't care).
  readonly joinMethod: JoinMethod;
  readonly message?: string;
};

export function useJoinGroup(): UseMutationResult<
  JoinGroupResponse,
  Error,
  JoinGroupInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, message }) => {
      const client = getApiClient();
      const body =
        message !== undefined && message.length > 0 ? { message } : {};
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/join',
        { params: { path: { id } }, body },
      ));
    },
    onMutate: async ({ id, kind }) => {
      await qc.cancelQueries({
        queryKey: kind === 'space' ? spacesKeys.all : groupKeys.all,
      });
      await qc.cancelQueries({ queryKey: groupKeys.viewerRole(id).queryKey });
      const prevRole = qc.getQueryData<ViewerGroupRole>(
        groupKeys.viewerRole(id).queryKey,
      );
      if (kind === 'space') {
        // Spaces don't have a request-pending state on the summary; the
        // server returns `joined` for open spaces and a 4xx for gated ones.
        // We optimistically flip to `member` and let `onSettled` correct on
        // failure.
        const snapshot = applySpaceJoinPatch(qc, id, 'member');
        qc.setQueryData<ViewerGroupRole>(groupKeys.viewerRole(id).queryKey, 'member');
        return { snapshot, prevRole };
      }
      const patch = predictJoinPatch(qc, id);
      const snapshot = applyJoinPatch(qc, id, patch);
      if (patch.membership !== undefined) {
        qc.setQueryData<ViewerGroupRole>(
          groupKeys.viewerRole(id).queryKey,
          patch.membership,
        );
      }
      return { snapshot, prevRole };
    },
    onError: (_err, { id }, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
      if (ctx?.prevRole !== undefined) {
        qc.setQueryData(groupKeys.viewerRole(id).queryKey, ctx.prevRole);
      }
    },
    onSuccess: (data, { kind, joinMethod }) => {
      // Only a genuinely new membership is a conversion. The server returns
      // kind:'joined' with alreadyMember:true for an idempotent re-join
      // (e.g. a double-click of the fire-and-forget join CTA) — firing there
      // would double-count one logical join.
      if (data.kind === 'joined' && !data.alreadyMember) {
        trackGroupJoined({
          group_type: groupKindToType(kind),
          role_on_join: data.role,
          join_method: joinMethod,
        });
      }
      // Circles and networks: requesting counts — the user took the action.
      // Spaces: require actual membership before marking done.
      const groupComplete = kind !== 'space' && (data.kind === 'joined' || data.kind === 'requested');
      const spaceComplete = kind === 'space' && data.kind === 'joined';
      if (!groupComplete && !spaceComplete) return;
      const actionKey =
        kind === 'circle' ? 'joined_circle'
        : kind === 'network' ? 'joined_network'
        : 'joined_group';
      void completeOnboardingActionIfPending(qc, actionKey);
    },
    onSettled: (_data, _err, { id, kind }) => {
      invalidateGroupAfterMutation(qc, kind, id, 'join');
    },
  });
}

// Unified leave hook for all three group kinds (Circle / Network / Space).
// All three now share the `/groups/{id}/leave` endpoint; the server
// dispatches on `groups.kind` for per-kind side effects (Circle conversation
// cleanup, Space's last-leader invariant). The `kind` argument stays on the
// hook input for cache-patching — the viewer-role cache
// (`groupKeys.viewerRole(id)`) is shared, but the per-kind detail / list
// keys aren't.
export type LeaveGroupInput = {
  readonly id: string;
  readonly kind: GroupKind;
};

export function useLeaveGroup(): UseMutationResult<void, Error, LeaveGroupInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: LeaveGroupInput) => {
      const client = getApiClient();
      unwrapVoid(await client.POST(
        '/api/v1/groups/{id}/leave',
        { params: { path: { id } } },
      ));
    },
    onMutate: async ({ id, kind }) => {
      await qc.cancelQueries({
        queryKey: kind === 'space' ? spacesKeys.all : groupKeys.all,
      });
      await qc.cancelQueries({ queryKey: groupKeys.viewerRole(id).queryKey });
      const prevRole = qc.getQueryData<ViewerGroupRole>(
        groupKeys.viewerRole(id).queryKey,
      );
      qc.setQueryData<ViewerGroupRole>(groupKeys.viewerRole(id).queryKey, null);
      const snapshot =
        kind === 'space'
          ? applySpaceJoinPatch(qc, id, null)
          : applyJoinPatch(qc, id, { membership: null });
      return { snapshot, prevRole };
    },
    onError: (_err, { id }, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
      if (ctx?.prevRole !== undefined) {
        qc.setQueryData(groupKeys.viewerRole(id).queryKey, ctx.prevRole);
      }
    },
    onSuccess: (_data, { kind }) => {
      trackGroupLeft({ group_type: groupKindToType(kind) });
    },
    onSettled: (_data, _err, { id, kind }) => {
      invalidateGroupAfterMutation(qc, kind, id, 'leave');
    },
  });
}

export type WithdrawMembershipRequestInput = {
  readonly id: string;
  readonly kind: 'circle' | 'network';
};

export function useWithdrawMembershipRequest(): UseMutationResult<
  void,
  Error,
  WithdrawMembershipRequestInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/groups/{id}/membership-requests/mine',
        { params: { path: { id } } },
      ));
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: groupKeys.all });
      const snapshot = applyJoinPatch(qc, id, { joinRequestStatus: null });
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
    },
    onSettled: (_data, _err, { id, kind }) => {
      // Detail only — withdraw doesn't change members/lists. Use the
      // kind-specific detail key.
      if (kind === 'circle') {
        qc.invalidateQueries({ queryKey: groupKeys.circle.detail(id).queryKey });
      } else {
        qc.invalidateQueries({ queryKey: groupKeys.network.detail(id).queryKey });
      }
    },
  });
}

// ---------- Leader-side: approve / decline / revoke membership-requests ------
//
// One unified surface under `/groups/:id/membership-requests/:reqId/*`.
// `approve` accepts a pending join-request (direction='request'); `decline`
// rejects it; `revoke` withdraws a pending invite the leader issued
// (direction='invite'). All three are kind-agnostic on the server; the hook
// carries `kind` only for cache patching.

export type ApproveMembershipRequestInput = {
  readonly groupId: string;
  readonly requestId: string;
  readonly kind: 'circle' | 'network';
};

export type DeclineMembershipRequestInput = ApproveMembershipRequestInput & {
  readonly reason?: string;
};

export function useApproveMembershipRequest(): UseMutationResult<
  unknown,
  Error,
  ApproveMembershipRequestInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, requestId }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/membership-requests/{reqId}/approve',
        { params: { path: { id: groupId, reqId: requestId } } },
      ));
    },
    onMutate: async ({ groupId, requestId, kind }) => {
      await qc.cancelQueries({
        queryKey:
          kind === 'circle'
            ? groupKeys.circle.joinRequests(groupId).queryKey
            : groupKeys.network.joinRequests(groupId).queryKey,
      });
      const snapshot = removeJoinRequestFromCache(qc, groupId, kind, requestId);
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
    },
    onSettled: (_data, _err, { groupId, kind }) => {
      invalidateGroupAfterMutation(qc, kind, groupId, 'request-decision');
    },
  });
}

export function useDeclineMembershipRequest(): UseMutationResult<
  unknown,
  Error,
  DeclineMembershipRequestInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, requestId, reason }) => {
      const client = getApiClient();
      const body = reason !== undefined ? { reason } : {};
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/membership-requests/{reqId}/decline',
        { params: { path: { id: groupId, reqId: requestId } }, body },
      ));
    },
    onMutate: async ({ groupId, requestId, kind }) => {
      await qc.cancelQueries({
        queryKey:
          kind === 'circle'
            ? groupKeys.circle.joinRequests(groupId).queryKey
            : groupKeys.network.joinRequests(groupId).queryKey,
      });
      const snapshot = removeJoinRequestFromCache(qc, groupId, kind, requestId);
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx?.snapshot);
    },
    onSettled: (_data, _err, { groupId, kind }) => {
      // Decline doesn't change members or detail — just refresh the
      // requests list. Use the narrow per-kind key to avoid a wider
      // invalidation than necessary.
      qc.invalidateQueries({
        queryKey:
          kind === 'circle'
            ? groupKeys.circle.joinRequests(groupId).queryKey
            : groupKeys.network.joinRequests(groupId).queryKey,
      });
    },
  });
}

// Leader withdraws a pending invite they issued (direction='invite'). Keyed by
// `(groupId, reqId)`; the membership-request row id is the invite id.
export function useRevokeMembershipInvite(): UseMutationResult<
  unknown,
  Error,
  { readonly groupId: string; readonly reqId: string; readonly kind?: 'circle' | 'network' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, reqId }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/membership-requests/{reqId}/revoke',
        { params: { path: { id: groupId, reqId } } },
      ));
    },
    onSettled: (_data, _err, { groupId, kind }) => {
      // The admin "Pending invites" table reads the invite slice of the
      // membership-request list, which is cached under the per-kind `invites`
      // prefix. Refresh it so the revoked row drops out.
      void qc.invalidateQueries({
        queryKey:
          kind === 'network'
            ? groupKeys.network.invites(groupId)
            : groupKeys.circle.invites(groupId),
      });
    },
  });
}

// ---------- Subject-side: accept / decline an invite addressed to me --------
//
// The durable consent surface (D6). The invitee Accepts or Declines a pending
// `direction='invite'` row from their Invitations list. Self-scoped on the
// server via a `subject_user_id = viewer` WHERE predicate — a non-subject
// loads zero rows → 404.

export type AcceptMembershipInviteResponse =
  components['schemas']['JoinGroupResponse'];

export function useAcceptMembershipInvite(): UseMutationResult<
  AcceptMembershipInviteResponse,
  Error,
  { readonly requestId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/membership-requests/{reqId}/accept',
        { params: { path: { reqId: requestId } } },
      ));
    },
    onSuccess: () => {
      // Membership shifted — drop the row from the pending-invites list and
      // refresh the viewer's group memberships + identity (mirrors the token
      // accept below).
      void qc.invalidateQueries({ queryKey: groupKeys.myPendingInvites().queryKey });
      void qc.invalidateQueries({ queryKey: ['groups'] });
      void qc.invalidateQueries({ queryKey: ['identity'] });
    },
  });
}

export function useDeclineMembershipInvite(): UseMutationResult<
  MembershipRequestView,
  Error,
  { readonly requestId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId }) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/membership-requests/{reqId}/decline',
        { params: { path: { reqId: requestId } } },
      ));
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: groupKeys.myPendingInvites().queryKey });
    },
  });
}

// ---------- Group members management (unified across circles/networks/spaces) ----------
//
// The endpoints themselves are kind-agnostic — the server resolves kind from
// the group id. We pass 'circle' to `invalidateGroupAfterMutation` as a
// sentinel; the helper's 'members' branch fans across all three kinds because
// it doesn't know which cache holds the row.

export function useSetGroupMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    // `kind` is analytics-only — the endpoint resolves the group's kind
    // from the id.
    mutationFn: async (input: {
      groupId: string;
      kind: GroupKind;
      userId: string;
      role: 'member' | 'leader';
    }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/members/{userId}/role',
        {
          params: { path: { id: input.groupId, userId: input.userId } },
          body: { role: input.role },
        },
      ));
    },
    onSuccess: (_data, vars) => {
      trackMemberRoleChanged({
        group_type: groupKindToType(vars.kind),
        new_role: vars.role,
      });
    },
    onSettled: (_data, _err, vars) =>
      invalidateGroupAfterMutation(qc, 'circle', vars.groupId, 'members'),
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { groupId: string; userId: string }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/groups/{id}/members/{userId}',
        { params: { path: { id: input.groupId, userId: input.userId } } },
      ));
    },
    onSettled: (_data, _err, vars) =>
      invalidateGroupAfterMutation(qc, 'circle', vars.groupId, 'members'),
  });
}

// ---------- Group invites (circle + network) ----------
//
// Both /api/v1/circles/{id}/* and /api/v1/networks/{id}/* expose the same
// per-recipient invite surfaces (lifted into shared services on the server
// in `service/group-invites.ts`). These hooks branch on `kind` so the
// `InviteMemberModal` can serve both kinds from one mount.

type GroupKindForInvites = 'circle' | 'network';

function inviteNoteKey(kind: GroupKindForInvites, groupId: string) {
  return kind === 'circle'
    ? groupKeys.circle.inviteNote(groupId).queryKey
    : groupKeys.network.inviteNote(groupId).queryKey;
}

// Persist the viewer's invite-blurb note for a (kind, groupId). Empty string
// deletes the row server-side; we still cache it as '' so subsequent reads
// don't re-fetch.
export function useSetGroupInviteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: GroupKindForInvites;
      groupId: string;
      note: string;
    }): Promise<string> => {
      const client = getApiClient();
      const body = { note: input.note };
      if (input.kind === 'circle') {
        const data = unwrap(await client.PUT(
          '/api/v1/circles/{id}/invite-prefs',
          { params: { path: { id: input.groupId } }, body },
        ));
        return data.note;
      }
      const data = unwrap(await client.PUT(
        '/api/v1/networks/{id}/invite-prefs',
        { params: { path: { id: input.groupId } }, body },
      ));
      return data.note;
    },
    onSuccess: (note, vars) => {
      qc.setQueryData(inviteNoteKey(vars.kind, vars.groupId), note);
    },
  });
}

// Per-email outcome of a create-invites batch (D-CONTRACT-6). One tagged entry
// per input email: `invited` (existing user, now a pending invite awaiting
// their acceptance), `emailed` (off-platform, sent a magic-link email),
// `auto_accepted` (mutual consent — a pending request existed, so they joined),
// `promoted` (already a member, role bumped to leader in place), or `skipped`.
export type CreateInvitesResponse =
  components['schemas']['CreateMembershipInvitesResponse'];
export type PerEmailOutcome = CreateInvitesResponse['results'][number];

export function useCreateGroupInvites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: GroupKindForInvites;
      groupId: string;
      emails: readonly string[];
      roleOnAccept?: 'member' | 'leader';
      note?: string;
    }): Promise<CreateInvitesResponse> => {
      const client = getApiClient();
      const body = {
        emails: [...input.emails],
        roleOnAccept: input.roleOnAccept ?? 'member',
        ...(input.note !== undefined ? { note: input.note } : {}),
      };
      // The endpoint is kind-agnostic now — the server resolves the group's
      // kind from the id. `kind` survives on the input only for cache keys.
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/invites',
        { params: { path: { id: input.groupId } }, body },
      ));
    },
    onSuccess: (_data, vars) => {
      trackMemberInvited({
        group_type: vars.kind,
        invite_count: vars.emails.length,
        has_note: vars.note !== undefined,
      });
    },
    onSettled: (data, _err, vars) => {
      // An `auto_accepted` or `promoted` outcome changed memberships, so the
      // member list + group detail / count need a refresh. Pure `invited` /
      // `emailed` / `skipped` batches leave memberships untouched.
      const membershipChanged =
        data !== undefined &&
        data.results.some(
          (r) => r.outcome === 'auto_accepted' || r.outcome === 'promoted',
        );
      if (membershipChanged) {
        invalidateGroupAfterMutation(qc, vars.kind, vars.groupId, 'members');
      }
      void qc.invalidateQueries({
        queryKey:
          vars.kind === 'circle'
            ? groupKeys.circle.invites(vars.groupId)
            : groupKeys.network.invites(vars.groupId),
      });
    },
  });
}

// Magic-link invite redemption (off-platform / signed-in token path). The token
// is opaque — the server resolves it to a pending off-platform invite row,
// verifies the email match, and grants membership. Returns `JoinGroupResponse`
// (`kind:'joined'`); the redeem page navigates using the invite-preview's
// group kind/id (the accept response carries only `groupId`).
export type AcceptInviteByTokenResponse =
  components['schemas']['JoinGroupResponse'];

export function useAcceptInviteByToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      token: string;
    }): Promise<AcceptInviteByTokenResponse> => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/invites/{token}/accept',
        { params: { path: { token: input.token } } },
      ));
    },
    onSuccess: () => {
      // The viewer's group membership list shifts after redemption — easier
      // to invalidate the whole groups slice + identity than to surgically
      // patch every list/detail entry the new membership touches.
      qc.invalidateQueries({ queryKey: ['groups'] });
      qc.invalidateQueries({ queryKey: ['identity'] });
    },
  });
}

// ---------- Group join link (canonical, auto-named) ----------
//
// Idempotent ensure behind the "Copy join link" button: returns the
// group's one canonical link, minting it (slug derived from the group
// name) on first use. Kind-agnostic — /groups/{groupId}/join-link serves
// circles and networks alike. Nothing cached: the caller copies the URL.

export function useEnsureGroupJoinLink(
  groupId: string,
): UseMutationResult<GroupJoinLink, Error, void> {
  return useMutation({
    mutationFn: async () => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/groups/{groupId}/join-link',
        { params: { path: { groupId } } },
      ));
    },
  });
}

// ---------- Group broadcasts (circle / network / space) ----------
//
// Group leaders create broadcasts under the per-scope `/circles|networks|
// spaces/{id}/broadcasts` URL family. Updates and previews use the canonical
// `/broadcasts/{id}` family — same URL the admin uses; Path B authz scopes
// the row by `audience_group_id`. The hooks below carry `kind`/`groupId`
// purely to derive the cache key, not to compose the URL.

type SendVia = 'email' | 'email_in_app';

// `scheduledSendAt` is meaningful only when scheduling, so it lives on the
// 'schedule' variant alone (invariant #4) — a 'schedule' with no time won't
// typecheck, and 'send'/'draft' can't carry a stray time.
export type CreateGroupBroadcastInput = {
  kind: BroadcastGroupKind;
  groupId: string;
  subject: string;
  bodyDoc: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
} & ({ mode: 'draft' | 'send' } | { mode: 'schedule'; scheduledSendAt: string });

export function useCreateGroupBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupBroadcastInput) => {
      const client = getApiClient();
      // The server's group-broadcast Zod schema models bodyDoc as
      // `z.object({}).passthrough()` (to avoid a layering cycle into the
      // platform doc schema), which the OpenAPI generator narrows to
      // `Record<string, never>`. The runtime contract is the full doc, so
      // we cast at this boundary — the platform doc schema validates it
      // server-side before any DB write.
      const body = {
        subject: input.subject,
        bodyDoc: input.bodyDoc as unknown as Record<string, never>,
        sendVia: 'email_in_app' as const,
        mode: input.mode,
        ...(input.preheaderText !== undefined
          ? { preheaderText: input.preheaderText }
          : {}),
        ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
        ...(input.mode === 'schedule'
          ? { scheduledSendAt: input.scheduledSendAt }
          : {}),
      } as const;
      return unwrap(await client.POST(
        '/api/v1/groups/{id}/broadcasts',
        { params: { path: { id: input.groupId } }, body },
      ));
    },
    onSuccess: (_data, vars) => {
      if (vars.mode !== 'draft') {
        trackBroadcastCreated({
          group_type: groupKindToType(vars.kind),
          is_scheduled: vars.mode === 'schedule',
        });
      }
      invalidateGroupAfterMutation(qc, vars.kind, vars.groupId, 'broadcast');
    },
  });
}

export interface UpdateGroupBroadcastInput {
  kind: BroadcastGroupKind;
  groupId: string;
  broadcastId: string;
  version: number;
  sendVia?: SendVia;
  subject?: string;
  bodyDoc?: BroadcastDoc;
  preheaderText?: string | null;
  replyTo?: string | null;
  scheduledSendAt?: string | null;
}

export function useUpdateGroupBroadcast(): UseMutationResult<
  AdminBroadcastDetail,
  Error,
  UpdateGroupBroadcastInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      const data = unwrap(await client.PATCH(
        '/api/v1/broadcasts/{id}',
        {
          params: { path: { id: input.broadcastId } },
          body: {
            version: input.version,
            ...(input.sendVia !== undefined ? { sendVia: input.sendVia } : {}),
            ...(input.subject !== undefined ? { subject: input.subject } : {}),
            ...(input.bodyDoc !== undefined ? { bodyDoc: input.bodyDoc } : {}),
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.replyTo !== undefined ? { replyTo: input.replyTo } : {}),
            ...(input.scheduledSendAt !== undefined
              ? { scheduledSendAt: input.scheduledSendAt }
              : {}),
          },
        },
      ));
      return data.data;
    },
    onSuccess: (data, vars) => {
      const scope = groupBroadcastScope(vars.kind, vars.groupId);
      qc.setQueryData(broadcastKeys.detail(scope, vars.broadcastId).queryKey, data);
      invalidateGroupAfterMutation(qc, vars.kind, vars.groupId, 'broadcast');
    },
  });
}

export interface PreviewGroupBroadcastInput {
  kind: BroadcastGroupKind;
  groupId: string;
  broadcastId: string;
  bodyDoc?: BroadcastDoc;
  subject?: string;
  preheaderText?: string | null;
  firstName?: string;
}

export function usePreviewGroupBroadcast(): UseMutationResult<
  BroadcastPreview,
  Error,
  PreviewGroupBroadcastInput
> {
  return useMutation({
    mutationFn: async (input) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/broadcasts/{id}/preview',
        {
          params: { path: { id: input.broadcastId } },
          body: {
            ...(input.bodyDoc !== undefined ? { bodyDoc: input.bodyDoc } : {}),
            ...(input.subject !== undefined ? { subject: input.subject } : {}),
            ...(input.preheaderText !== undefined
              ? { preheaderText: input.preheaderText }
              : {}),
            ...(input.firstName !== undefined
              ? { firstName: input.firstName }
              : {}),
          },
        },
      ));
      return data.data;
    },
  });
}

// ---------- Networks: create / update / posts ----------

export type CreateNetworkInput = {
  name: string;
  description: string;
  region: 'americas' | 'apac' | 'emea' | 'global_affinity';
  access: 'public' | 'unlisted' | 'private';
  minReadRole: 'anyone' | 'member' | 'leader';
  tagline?: string;
  mission?: string;
  welcomeMessage?: string;
  memberRules?: string;
  joinInstructions?: string;
  tags?: readonly string[];
  // Strict object — the server's SocialLinksSchema rejects unknown keys and
  // empty strings. Omit a key to leave that link unset.
  socialLinks?: {
    website?: string;
    linkedin?: string;
    instagram?: string;
    facebook?: string;
    twitter?: string;
    email?: string;
  };
};

export function useCreateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNetworkInput) => {
      const client = getApiClient();
      const data = unwrap(await client.POST('/api/v1/networks', {
        body: {
          name: input.name,
          description: input.description,
          region: input.region,
          access: input.access,
          minReadRole: input.minReadRole,
          tagline: input.tagline,
          mission: input.mission,
          welcomeMessage: input.welcomeMessage,
          memberRules: input.memberRules,
          joinInstructions: input.joinInstructions,
          tags: input.tags !== undefined ? [...input.tags] : undefined,
          socialLinks: input.socialLinks,
        },
      }));
      return data.data;
    },
    onSuccess: (_data, vars) => {
      // No join_policy on networks — the field doesn't exist on the input.
      trackGroupCreated({
        group_type: 'network',
        ...(vars.access !== undefined ? { access: vars.access } : {}),
      });
      qc.invalidateQueries({ queryKey: groupKeys.network.lists() });
      // "My networks" filter view is usually inactive when a create fires
      // (admin is browsing /networks). Default invalidate marks it stale but
      // doesn't refetch — the next tab switch would flash stale rows.
      // Opts-less prefix so every { includeArchived, pageSize } variant refetches.
      invalidateAndRefetchAll(qc, { queryKey: [...groupKeys.network.all, 'mine'] });
    },
  });
}

export type NetworkLeaderApplicationInput = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  countryCode?: string;
  region: 'EMEA' | 'Americas' | 'APAC' | 'Global Affinity';
  phone?: string;
  circles?: string;
  otherCommunity?: string;
  communitySize?: string;
  proposedName: string;
  city: string;
  whyCreate: string;
  eventExperience: string;
  leadershipPlan: string;
};

export function useSubmitNetworkLeaderApplication() {
  return useMutation({
    mutationFn: async (input: NetworkLeaderApplicationInput) => {
      const client = getApiClient();
      unwrapVoid(await client.POST('/api/v1/networks/apply', { body: input }));
    },
    onSuccess: () => {
      trackNetworkLeaderApplicationSubmitted();
    },
  });
}

export type UpdateNetworkInput = {
  networkId: string;
  name?: string;
  description?: string;
  access?: 'public' | 'unlisted' | 'private';
  minReadRole?: 'anyone' | 'member' | 'leader';
  imageUrl?: string | null;
  topicIds?: readonly string[];
  region?: 'americas' | 'apac' | 'emea' | 'global_affinity';
  // The admin surface supports additional fields the schema accepts.
  tagline?: string | null;
  mission?: string | null;
  welcomeMessage?: string | null;
  memberRules?: string | null;
  joinInstructions?: string | null;
  logoUrl?: string | null;
  tags?: readonly string[];
  // Strict object — the server's SocialLinksSchema rejects unknown keys. Use
  // an empty object (not undefined) to wipe all social links in one go.
  socialLinks?: {
    website?: string;
    linkedin?: string;
    instagram?: string;
    facebook?: string;
    twitter?: string;
    email?: string;
  };
};

export function useUpdateNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateNetworkInput) => {
      const client = getApiClient();
      return unwrap(await client.PATCH('/api/v1/networks/{id}', {
        params: { path: { id: input.networkId } },
        body: {
          name: input.name,
          description: input.description,
          access: input.access,
          minReadRole: input.minReadRole,
          imageUrl: input.imageUrl,
          topicIds:
            input.topicIds !== undefined ? [...input.topicIds] : undefined,
          region: input.region,
          tagline: input.tagline,
          mission: input.mission,
          welcomeMessage: input.welcomeMessage,
          memberRules: input.memberRules,
          joinInstructions: input.joinInstructions,
          logoUrl: input.logoUrl,
          tags: input.tags !== undefined ? [...input.tags] : undefined,
          socialLinks: input.socialLinks,
        },
      }));
    },
    onSuccess: (_data, vars) => {
      invalidateGroupAfterMutation(qc, 'network', vars.networkId, 'update');
    },
  });
}

// ---------- Networks: link / unlink circles ----------

export function useLinkCircleToNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { networkId: string; circleId: string }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/networks/{id}/circles',
        {
          params: { path: { id: input.networkId } },
          body: { circleId: input.circleId },
        },
      ));
    },
    onSettled: (_data, _err, vars) => {
      invalidateAfterCircleNetworkLink(qc, vars.networkId, vars.circleId);
    },
  });
}

export function useUnlinkCircleFromNetwork() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { networkId: string; circleId: string }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/networks/{id}/circles/{circleId}',
        {
          params: {
            path: { id: input.networkId, circleId: input.circleId },
          },
        },
      ));
    },
    onSettled: (_data, _err, vars) => {
      invalidateAfterCircleNetworkLink(qc, vars.networkId, vars.circleId);
    },
  });
}

// ---------- Network resources (featured / delete) ----------

export function useFeatureNetworkResource() {
  return useMutation({
    mutationFn: async (input: {
      networkId: string;
      resourceId: string;
      featured: boolean;
    }) => {
      const client = getApiClient();
      if (input.featured) {
        unwrapVoid(await client.POST(
          '/api/v1/networks/{id}/resources/{resourceId}/feature',
          {
            params: {
              path: { id: input.networkId, resourceId: input.resourceId },
            },
          },
        ));
      } else {
        unwrapVoid(await client.DELETE(
          '/api/v1/networks/{id}/resources/{resourceId}/feature',
          {
            params: {
              path: { id: input.networkId, resourceId: input.resourceId },
            },
          },
        ));
      }
    },
  });
}

export function useDeleteNetworkResource() {
  return useMutation({
    mutationFn: async (input: {
      networkId: string;
      resourceId: string;
    }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/networks/{id}/resources/{resourceId}',
        {
          params: {
            path: { id: input.networkId, resourceId: input.resourceId },
          },
        },
      ));
    },
  });
}

// Re-export the unified MembershipRequestView so consumers don't need to chase
// down the schema namespace just to type a row.
export type { MembershipRequestView };
